import React, {
  cloneElement,
  FC,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  useEffect,
} from "react";
import { useDispatch } from "react-redux";
import { useImmer } from "use-immer";

import { IgTooltip } from "@intelligems/ig-theme";
import { IntelligemsAppScopeType } from "@intelligems/ig-types";

import { useIsAdmin } from "../../api/user-auths/is-admin";
import { setDialog } from "../../store/slices/global";

import { useHasRequiredShopifyScopes } from "./hooks";
import { ADMINS_HAVE_FULL_ACCESS } from "./Scopes";

export interface FeatureToggleAnyWithDisableProps {
  children: ReactElement<
    {
      disabled: boolean;
      onClick?: () => void;
      onChange?: () => void;
    },
    string | JSXElementConstructor<any>
  >;
  isEnabled: boolean;
  adminDisable?: boolean; // when a component should be disabled for admins too
  customComponentWhenDisabled?: ReactNode;
  customDisabledTooltipMessage?: ReactNode;
  debugId: string;
  requiredShopifyScopes?: IntelligemsAppScopeType[];
  disableTooltip?: boolean;
  customEnabledTooltipMessage?: ReactNode;
  modalPopupProps?: {
    title: string;
    message: string;
    yesButtonText?: string;
    noButtonText?: string;
    yesButtonAction?: () => void;
    noButtonAction?: () => void;
    showButtons?: boolean;
  };
}

export const ScopedComponentWithDisable: FC<
  FeatureToggleAnyWithDisableProps
> = ({
  children,
  customComponentWhenDisabled,
  debugId,
  requiredShopifyScopes,
  customDisabledTooltipMessage,
  isEnabled,
  modalPopupProps,
  adminDisable,
  disableTooltip,
  customEnabledTooltipMessage,
  ...props
}) => {
  const isAdmin = useIsAdmin();
  const dispatch = useDispatch();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes(
    requiredShopifyScopes || []
  );
  const doAdminsHaveAccess =
    isAdmin && ADMINS_HAVE_FULL_ACCESS && !adminDisable;

  const [previousEnabled, setPreviousEnabled] = useImmer<boolean | undefined>(
    undefined
  );

  useEffect(() => {
    if (previousEnabled === undefined) {
      setPreviousEnabled(isEnabled);
    }
  }, [isEnabled]);

  useEffect(() => {
    if (
      previousEnabled &&
      !doAdminsHaveAccess &&
      !isEnabled &&
      modalPopupProps
    ) {
      setPreviousEnabled(isEnabled);
      dispatch(
        setDialog({
          dialog: "scoped",
          open: true,
          props: {
            title: modalPopupProps.title,
            message: modalPopupProps.message,
            yesButtonText: modalPopupProps.yesButtonText,
            yesButtonAction: modalPopupProps.yesButtonAction,
            noButtonText: modalPopupProps.noButtonText,
            noButtonAction: modalPopupProps.noButtonAction,
            showButtons: modalPopupProps.showButtons || true,
          },
        })
      );
    }
  }, [isEnabled]);

  if (!hasRequiredShopifyScopes) {
    return (
      <span>
        {cloneElement(children, {
          disabled: false,
          onChange: () => {
            if (children.props.onChange) {
              dispatch(
                setDialog({ dialog: "missingShopifyScope", open: true })
              );
            }
          },
          onClick: () => {
            if (children.props.onClick) {
              dispatch(
                setDialog({ dialog: "missingShopifyScope", open: true })
              );
            }
          },
        })}
      </span>
    );
  }

  if (customComponentWhenDisabled && !isEnabled && !doAdminsHaveAccess) {
    return (
      <IgTooltip
        title={
          customEnabledTooltipMessage
            ? customEnabledTooltipMessage
            : isEnabled || doAdminsHaveAccess || disableTooltip
              ? ""
              : customDisabledTooltipMessage ||
                "Please contact Intelligems to unlock this feature"
        }
      >
        <span>{customComponentWhenDisabled}</span>
      </IgTooltip>
    );
  }

  return (
    <IgTooltip
      title={
        customEnabledTooltipMessage && !doAdminsHaveAccess
          ? customEnabledTooltipMessage
          : isEnabled || doAdminsHaveAccess || disableTooltip
            ? ""
            : customDisabledTooltipMessage ||
              "Please contact Intelligems to unlock this feature"
      }
    >
      <span>
        {!isEnabled && !doAdminsHaveAccess
          ? cloneElement(children, {
              disabled: true,
            })
          : cloneElement(children, { disabled: false })}
      </span>
    </IgTooltip>
  );
};
